/**
 * Chat Group Button
 *
 * Header control for creating and managing group chats.
 *
 * Primary responsibilities:
 * - Convert the current chat into a group chat
 * - Display invite-link controls for group chats
 * - Display member list with admin/member actions
 *
 * Dependencies:
 * - Chat and app contexts
 * - Group chat client service
 * - Realtime auth helper
 * - Dialog and button UI components
 */

'use client'

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useSetAtom } from 'jotai'
import { toast } from 'sonner'
import {
  IconArchive,
  IconArchiveOff,
  IconCrown,
  IconDots,
  IconLink,
  IconRefresh,
  IconSchool,
  IconShield,
  IconTrash,
  IconUsers
} from '@tabler/icons-react'

import { ChatbotUIChatContext } from '@/context/chat'
import { useAuth } from '@/context/auth'
import { ChatbotUIContext } from '@/context/context'
import {
  removeChatAtom,
  updateChatAtom
} from '@/atoms/workspaceAtoms'
import {
  getGroupChatResponseMode
} from '@/lib/chat/group-chat-response-mode'
import {
  GROUP_CHAT_SHOW_ARCHIVED_EVENT,
  GROUP_CHAT_SHOW_ARCHIVED_STORAGE_KEY,
  getGroupChatStackMetadata,
  isGroupChatArchived
} from '@/lib/chat/group-chat-display'
import {
  getRealtimeReconnectDelay,
  isRealtimeChannelFailure,
  removeRealtimeChannelOnce,
  shouldRemoveRealtimeChannel
} from '@/lib/realtime/channel-recovery'
import { syncPrivateRealtimeAuth } from '@/lib/realtime/private-channel-auth'
import { getWorkspaceRoleForUser } from '@/lib/workspaces/workspace-access'
import { cn } from '@/lib/utils'
import { safeConsole } from '@/lib/utils/safeConsole'
import {
  getGroupInviteLink,
  getGroupMembers,
  regenerateGroupInviteLink,
  runGroupMembersAction,
  updateGroupResponseMode
} from '@/lib/services/chat-group-service'
import { createClient } from '@/lib/supabase/client'
import type { Tables } from '@/supabase/types'
import type {
  GroupChatInviteLink,
  GroupChatMemberSummary,
  GroupChatResponseMode
} from '@/types/chat-groups'
import { useSplitPaneNavigation } from '@/components/splitview/split-pane-context'
import { Button } from '@/components/ui/button'
import { CopyButton } from '@/components/ui/copy-button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'

function resolveMemberName(member: GroupChatMemberSummary) {
  const displayName = member.displayName?.trim()
  if (displayName) return displayName

  const email = member.email?.trim()
  if (email) return email

  return 'Member'
}

function resolveInviteUrl(inviteLink: GroupChatInviteLink | null) {
  if (!inviteLink) {
    return ''
  }

  if (typeof window === 'undefined') {
    return inviteLink.invitePath
  }

  return `${window.location.origin}${inviteLink.invitePath}`
}

function getRoleLabel(role: string | null) {
  if (role === 'owner') return 'Owner'
  if (role === 'admin') return 'Admin'
  if (role === 'tutor') return 'Tutor'
  if (role === 'member') return 'Member'
  if (role === 'viewer') return 'Viewer'
  return role || 'Member'
}

type ChatGroupButtonViewport = 'always' | 'desktop' | 'mobile'

type ChatGroupButtonProps = {
  realtimeViewport?: ChatGroupButtonViewport
}

function matchesRealtimeViewport(viewport: ChatGroupButtonViewport) {
  if (viewport === 'always') return true
  if (typeof window === 'undefined' || !window.matchMedia) return false

  const isDesktop = window.matchMedia('(min-width: 768px)').matches
  return viewport === 'desktop' ? isDesktop : !isDesktop
}

function canKeepRealtimeOpen(viewport: ChatGroupButtonViewport) {
  if (!matchesRealtimeViewport(viewport)) return false
  if (typeof document === 'undefined') return true
  return document.visibilityState !== 'hidden'
}

function useChatGroupButtonRealtimeActive(viewport: ChatGroupButtonViewport) {
  const [isActive, setIsActive] = useState(() => canKeepRealtimeOpen(viewport))

  useEffect(() => {
    if (typeof window === 'undefined') {
      setIsActive(canKeepRealtimeOpen(viewport))
      return
    }

    const updateActiveState = () => {
      setIsActive(canKeepRealtimeOpen(viewport))
    }
    const mediaQuery =
      viewport === 'always' ? null : window.matchMedia('(min-width: 768px)')

    updateActiveState()
    mediaQuery?.addEventListener('change', updateActiveState)
    document.addEventListener('visibilitychange', updateActiveState)
    window.addEventListener('pagehide', updateActiveState)
    window.addEventListener('pageshow', updateActiveState)

    return () => {
      mediaQuery?.removeEventListener('change', updateActiveState)
      document.removeEventListener('visibilitychange', updateActiveState)
      window.removeEventListener('pagehide', updateActiveState)
      window.removeEventListener('pageshow', updateActiveState)
    }
  }, [viewport])

  return isActive
}

const avatarFallbackClasses = [
  'bg-stone-900 text-stone-50',
  'bg-sky-900 text-sky-50',
  'bg-emerald-900 text-emerald-50',
  'bg-rose-900 text-rose-50',
  'bg-amber-900 text-amber-50'
]

function getMemberSortRank(member: GroupChatMemberSummary) {
  if (member.isCreator || member.role === 'owner') return 0
  if (member.role === 'admin') return 1
  if (member.role === 'tutor') return 2
  return 3
}

function sortGroupMembers(members: GroupChatMemberSummary[]) {
  return [...members].sort((left, right) => {
    const rankDelta = getMemberSortRank(left) - getMemberSortRank(right)

    if (rankDelta !== 0) {
      return rankDelta
    }

    return resolveMemberName(left).localeCompare(resolveMemberName(right))
  })
}

function getMemberInitials(member: GroupChatMemberSummary) {
  const name = resolveMemberName(member)
  const parts = name
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean)

  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }

  return name.slice(0, 2).toUpperCase()
}

function GroupMemberAvatar({
  member,
  index,
  className
}: {
  member: GroupChatMemberSummary
  index: number
  className?: string
}) {
  return (
    <Avatar className={cn('border-background size-8 border-2 shadow-sm', className)}>
      <AvatarImage alt={resolveMemberName(member)} src={member.imageUrl || undefined} />
      <AvatarFallback
        className={cn('text-[10px] font-semibold', avatarFallbackClasses[index % avatarFallbackClasses.length])}
      >
        {getMemberInitials(member)}
      </AvatarFallback>
    </Avatar>
  )
}

function GroupAvatarStack({
  members,
  isLoading,
  currentUserId,
  size = 'default'
}: {
  members: GroupChatMemberSummary[]
  isLoading: boolean
  currentUserId: string | null
  size?: 'default' | 'compact'
}) {
  const visibleMembers = members
    .filter((member) => member.userId !== currentUserId)
    .slice(0, 4)
  const avatarSize = size === 'compact' ? 'size-7' : 'size-8'

  if (!visibleMembers.length && isLoading) {
    return (
      <div className="flex -space-x-2">
        {[0, 1, 2].map((index) => (
          <span
            aria-hidden="true"
            className={cn(
              'border-background bg-muted block rounded-full border-2 shadow-sm',
              avatarSize
            )}
            key={index}
          />
        ))}
      </div>
    )
  }

  if (!visibleMembers.length) {
    return (
      <div
        className={cn(
          'border-background bg-muted text-muted-foreground flex items-center justify-center rounded-full border-2 shadow-sm',
          avatarSize
        )}
      >
        <IconUsers className="size-3.5" stroke={1.8} />
      </div>
    )
  }

  return (
    <div className="flex -space-x-2">
      {visibleMembers.map((member, index) => (
        <div key={member.userId} style={{ zIndex: visibleMembers.length - index }}>
          <GroupMemberAvatar
            className={avatarSize}
            index={index}
            member={member}
          />
        </div>
      ))}
    </div>
  )
}

export function ChatGroupButton({
  realtimeViewport = 'always'
}: ChatGroupButtonProps = {}) {
  const { navigateToHref } = useSplitPaneNavigation()
  const { user } = useAuth()
  const { selectedChat, setSelectedChat } = useContext(ChatbotUIChatContext)
  const { chats, profile, setChats } = useContext(ChatbotUIContext)
  const updateChatInAtom = useSetAtom(updateChatAtom)
  const removeChatFromAtom = useSetAtom(removeChatAtom)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [members, setMembers] = useState<GroupChatMemberSummary[]>([])
  const [inviteLink, setInviteLink] = useState<GroupChatInviteLink | null>(null)
  const [inviteLinkError, setInviteLinkError] = useState<string | null>(null)
  const [isRefreshingData, setIsRefreshingData] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [responseMode, setResponseMode] = useState<GroupChatResponseMode>('always')
  const [serverCanManage, setServerCanManage] = useState<boolean | null>(null)
  const [canAssignTutorRole, setCanAssignTutorRole] = useState(false)
  const [loadedChatId, setLoadedChatId] = useState<string | null>(null)
  const [workspaceRole, setWorkspaceRole] = useState<string | null>(null)
  const [isWorkspaceRoleLoading, setIsWorkspaceRoleLoading] = useState(false)
  const [reconnectNonce, setReconnectNonce] = useState(0)
  const [showArchivedGroups, setShowArchivedGroups] = useState(false)
  const refreshRequestIdRef = useRef(0)
  const reconnectAttemptRef = useRef(0)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isRealtimeActive = useChatGroupButtonRealtimeActive(realtimeViewport)

  const currentUserId = user?.id ?? profile?.user_id ?? null
  const selectedChatId = selectedChat?.id ?? null
  const isGroupChat = Boolean(selectedChat?.is_group_chat)
  const currentMember = useMemo(
    () => members.find((member) => member.userId === currentUserId) ?? null,
    [currentUserId, members]
  )
  const canManageGroup = useMemo(() => {
    if (serverCanManage !== null) {
      return serverCanManage
    }

    if (!selectedChat || !currentUserId) {
      return false
    }

    if (selectedChat.user_id === currentUserId) {
      return true
    }

    return currentMember?.role === 'owner' || currentMember?.role === 'admin'
  }, [currentMember?.role, currentUserId, selectedChat, serverCanManage])

  const hasFreshGroupData = Boolean(selectedChat?.id && loadedChatId === selectedChat.id)
  const isWorkspaceManager = workspaceRole === 'owner' || workspaceRole === 'admin'
  const isArchivedGroup = selectedChat ? isGroupChatArchived(selectedChat) : false

  const inviteUrl = useMemo(() => resolveInviteUrl(inviteLink), [inviteLink])
  const isManageStatePending = isGroupChat && (!hasFreshGroupData || isRefreshingData)
  const orderedMembers = useMemo(() => sortGroupMembers(members), [members])
  const hasTutorMember = useMemo(
    () => orderedMembers.some((member) => member.role === 'tutor'),
    [orderedMembers]
  )
  const memberCount = selectedChat?.participant_count || orderedMembers.length
  const stackMetadata = selectedChat
    ? getGroupChatStackMetadata(selectedChat.metadata)
    : null
  const stackSelectValue = stackMetadata?.parentChatId ?? '__none__'
  const stackTargetOptions = useMemo(
    () =>
      chats
        .filter(
          (chat) =>
            chat.is_group_chat &&
            chat.id !== selectedChatId &&
            chat.workspace_id === selectedChat?.workspace_id
        )
        .sort((left, right) => {
          const leftTime = Date.parse(left.updated_at || left.created_at || '')
          const rightTime = Date.parse(right.updated_at || right.created_at || '')
          return (Number.isFinite(rightTime) ? rightTime : 0) -
            (Number.isFinite(leftTime) ? leftTime : 0)
        }),
    [chats, selectedChat?.workspace_id, selectedChatId]
  )

  const syncChatState = useCallback((nextChat: Tables<'chats'>) => {
    setSelectedChat((previousChat) =>
      previousChat?.id === nextChat.id ? nextChat : previousChat
    )

    setChats((previousChats) => {
      const hasExistingChat = previousChats.some((chat) => chat.id === nextChat.id)

      if (!hasExistingChat) {
        return [nextChat, ...previousChats]
      }

      return previousChats.map((chat) => (chat.id === nextChat.id ? nextChat : chat))
    })

    updateChatInAtom(nextChat)
  }, [setChats, setSelectedChat, updateChatInAtom])

  const clearDialogGroupData = useCallback(() => {
    setMembers([])
    setInviteLink(null)
    setInviteLinkError(null)
    setErrorMessage(null)
    setCanAssignTutorRole(false)
  }, [])

  const clearLoadedGroupData = useCallback(() => {
    clearDialogGroupData()
    setServerCanManage(null)
    setLoadedChatId(null)
  }, [clearDialogGroupData])

  const applyGroupState = useCallback((
    snapshot: Awaited<ReturnType<typeof getGroupMembers>>,
    options?: {
      includeMembers?: boolean
    }
  ) => {
    syncChatState(snapshot.chat)

    if (options?.includeMembers !== false) {
      setMembers(snapshot.members)
    }

    setResponseMode(snapshot.responseMode)
    setServerCanManage(snapshot.canManage)
    setCanAssignTutorRole(snapshot.canAssignTutorRole)
    setLoadedChatId(snapshot.chat.id)
  }, [syncChatState])

  const syncChatParticipantCount = useCallback((participantCount?: number) => {
    if (!selectedChat?.id || typeof participantCount !== 'number') {
      return
    }

    const nextChat = {
      ...selectedChat,
      participant_count: participantCount
    }

    syncChatState(nextChat)
  }, [selectedChat, syncChatState])

  useEffect(() => {
    try {
      setShowArchivedGroups(
        localStorage.getItem(GROUP_CHAT_SHOW_ARCHIVED_STORAGE_KEY) === 'true'
      )
    } catch {
      setShowArchivedGroups(false)
    }
  }, [])

  const handleToggleArchivedGroups = (checked: boolean) => {
    setShowArchivedGroups(checked)

    try {
      localStorage.setItem(GROUP_CHAT_SHOW_ARCHIVED_STORAGE_KEY, String(checked))
      window.dispatchEvent(
        new CustomEvent(GROUP_CHAT_SHOW_ARCHIVED_EVENT, {
          detail: { showArchived: checked }
        })
      )
    } catch {}
  }

  const refreshGroupData = useCallback(async () => {
    if (!selectedChatId || !isGroupChat) {
      return
    }

    const requestId = refreshRequestIdRef.current + 1
    refreshRequestIdRef.current = requestId
    setErrorMessage(null)

    if (loadedChatId !== selectedChatId) {
      clearDialogGroupData()
    }

    setIsRefreshingData(true)

    try {
      const membersResponse = await getGroupMembers(selectedChatId)
      if (refreshRequestIdRef.current !== requestId) {
        return
      }

      applyGroupState(membersResponse)

      if (!membersResponse.canManage) {
        setInviteLink(null)
        setInviteLinkError(null)
        return
      }

      try {
        const inviteResponse = await getGroupInviteLink(selectedChatId)
        if (refreshRequestIdRef.current !== requestId) {
          return
        }

        setInviteLink(inviteResponse.inviteLink)
        setInviteLinkError(null)
      } catch (error) {
        if (refreshRequestIdRef.current !== requestId) {
          return
        }

        const message = error instanceof Error ? error.message : 'Failed to load invite link'
        setInviteLink(null)
        setInviteLinkError(message)
      }
    } catch (error) {
      if (refreshRequestIdRef.current !== requestId) {
        return
      }

      const message = error instanceof Error ? error.message : 'Failed to load group chat details'
      setErrorMessage(message)
    } finally {
      if (refreshRequestIdRef.current === requestId) {
        setIsRefreshingData(false)
      }
    }
  }, [
    applyGroupState,
    clearDialogGroupData,
    isGroupChat,
    loadedChatId,
    selectedChatId,
  ])

  const hydrateSelectedGroupState = useCallback(async () => {
    if (!selectedChatId || !isGroupChat || isDialogOpen) {
      return
    }

    if (loadedChatId !== selectedChatId) {
      clearDialogGroupData()
    }

    const requestId = refreshRequestIdRef.current + 1
    refreshRequestIdRef.current = requestId

    try {
      const membersResponse = await getGroupMembers(selectedChatId)
      if (refreshRequestIdRef.current !== requestId) {
        return
      }

      applyGroupState(membersResponse)
    } catch {}
  }, [
    applyGroupState,
    clearDialogGroupData,
    isDialogOpen,
    isGroupChat,
    loadedChatId,
    selectedChatId
  ])

  useEffect(() => {
    if (!isGroupChat || typeof window === 'undefined') {
      return
    }

    const url = new URL(window.location.href)
    if (url.searchParams.get('groupSettings') !== '1') {
      return
    }

    setIsDialogOpen(true)
    url.searchParams.delete('groupSettings')
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
  }, [isGroupChat, selectedChatId])

  useEffect(() => {
    if (!isGroupChat) {
      refreshRequestIdRef.current += 1
      setIsRefreshingData(false)
      clearLoadedGroupData()
      return
    }

    if (!isDialogOpen) {
      refreshRequestIdRef.current += 1
      setIsRefreshingData(false)
      return
    }

    void refreshGroupData()
  }, [
    clearDialogGroupData,
    clearLoadedGroupData,
    isDialogOpen,
    isGroupChat,
    refreshGroupData
  ])

  useEffect(() => {
    if (!selectedChatId || !isGroupChat || isDialogOpen) {
      return
    }

    void hydrateSelectedGroupState()
  }, [hydrateSelectedGroupState, isDialogOpen, isGroupChat, selectedChatId])

  useEffect(() => {
    if (!currentUserId || !isGroupChat || !selectedChatId || !isRealtimeActive) {
      return
    }

    const supabase = createClient()
    let isDisposed = false
    let refreshTimeout: ReturnType<typeof setTimeout> | null = null
    const clearReconnectTimer = () => {
      if (!reconnectTimeoutRef.current) {
        return
      }

      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    const scheduleReconnect = () => {
      if (isDisposed) {
        return
      }

      clearReconnectTimer()

      const delay = getRealtimeReconnectDelay(reconnectAttemptRef.current)
      reconnectAttemptRef.current += 1
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null
        setReconnectNonce((value) => value + 1)
      }, delay)
    }
    const queueRefresh = () => {
      if (refreshTimeout) {
        clearTimeout(refreshTimeout)
      }

      refreshTimeout = setTimeout(() => {
        if (isDialogOpen) {
          void refreshGroupData()
          return
        }

        void hydrateSelectedGroupState()
      }, 200)
    }

    const channel = supabase
      .channel(`chat-group-settings:${selectedChatId}`, {
        config: {
          private: true
        }
      })
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_members',
          filter: `chat_id=eq.${selectedChatId}`
        },
        queueRefresh
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_invitations',
          filter: `chat_id=eq.${selectedChatId}`
        },
        queueRefresh
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chats',
          filter: `id=eq.${selectedChatId}`
        },
        queueRefresh
      )

    void syncPrivateRealtimeAuth(supabase)
      .then((isReady) => {
        if (!isReady || isDisposed) {
          if (!isReady && !isDisposed) {
            queueRefresh()
            scheduleReconnect()
          }

          return
        }

        channel.subscribe((status) => {
          if (isDisposed) {
            return
          }

          if (status === 'SUBSCRIBED') {
            reconnectAttemptRef.current = 0
            clearReconnectTimer()
            queueRefresh()
            return
          }

          if (isRealtimeChannelFailure(status)) {
            safeConsole.warn('Chat group settings realtime channel failed', {
              chatId: selectedChatId,
              status,
              userId: currentUserId
            })
            queueRefresh()
            if (shouldRemoveRealtimeChannel(status)) {
              void removeRealtimeChannelOnce(supabase, channel)
            }
            scheduleReconnect()
          }
        })
      })
      .catch((error) => {
        if (!isDisposed) {
          safeConsole.warn('Chat group settings realtime auth sync failed', error)
          queueRefresh()
          scheduleReconnect()
        }
      })

    return () => {
      isDisposed = true

      if (refreshTimeout) {
        clearTimeout(refreshTimeout)
      }

      clearReconnectTimer()
      void removeRealtimeChannelOnce(supabase, channel)
    }
  }, [
    currentUserId,
    hydrateSelectedGroupState,
    isDialogOpen,
    isGroupChat,
    isRealtimeActive,
    reconnectNonce,
    refreshGroupData,
    selectedChatId
  ])

  useEffect(() => {
    if (!selectedChat?.is_group_chat) {
      setResponseMode('always')
      clearLoadedGroupData()
      return
    }

    setResponseMode(getGroupChatResponseMode(selectedChat.metadata))
  }, [selectedChat?.id, selectedChat?.is_group_chat, selectedChat?.metadata])

  useEffect(() => {
    if (!selectedChat?.workspace_id || !currentUserId) {
      setWorkspaceRole(null)
      setIsWorkspaceRoleLoading(false)
      return
    }

    if (selectedChat.user_id === currentUserId) {
      setWorkspaceRole('owner')
      setIsWorkspaceRoleLoading(false)
      return
    }

    let isCancelled = false
    const supabase = createClient()
    setIsWorkspaceRoleLoading(true)

    getWorkspaceRoleForUser(
      supabase,
      selectedChat.workspace_id,
      currentUserId
    )
      .then((role) => {
        if (!isCancelled) {
          setWorkspaceRole(role)
          setIsWorkspaceRoleLoading(false)
        }
      })
      .catch(() => {
        if (!isCancelled) {
          setWorkspaceRole(null)
          setIsWorkspaceRoleLoading(false)
        }
      })

    return () => {
      isCancelled = true
    }
  }, [
    currentUserId,
    selectedChat?.id,
    selectedChat?.user_id,
    selectedChat?.workspace_id
  ])

  const handleRegenerateInviteLink = async () => {
    if (!selectedChat?.id || !canManageGroup || !hasFreshGroupData) {
      return
    }

    setIsLoading(true)
    setErrorMessage(null)

    try {
      const response = await regenerateGroupInviteLink(selectedChat.id)
      setInviteLink(response.inviteLink)
      setInviteLinkError(null)
      toast.success('Invite link regenerated')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to regenerate invite link'
      setErrorMessage(message)
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpdateMemberRole = async (
    member: GroupChatMemberSummary,
    role: 'admin' | 'member' | 'tutor'
  ) => {
    if (!selectedChat?.id || !canManageGroup || !hasFreshGroupData || member.isCreator) {
      return
    }

    if ((role === 'tutor' || member.role === 'tutor') && !canAssignTutorRole) {
      return
    }

    setIsLoading(true)
    setErrorMessage(null)

    try {
      const response = await runGroupMembersAction(selectedChat.id, {
        action: 'update_role',
        userId: member.userId,
        role
      })

      if (response.chat) {
        syncChatState(response.chat)
      }

      if (response.responseMode) {
        setResponseMode(response.responseMode)
      }

      await refreshGroupData()
      toast.success(
        role === 'tutor'
          ? 'Tutor role updated. Losi replies now require @Losi.'
          : 'Member role updated'
      )
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update member role'
      setErrorMessage(message)
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleRemoveMember = async (member: GroupChatMemberSummary) => {
    if (!selectedChat?.id || !canManageGroup || !hasFreshGroupData || member.isCreator) {
      return
    }

    setIsLoading(true)
    setErrorMessage(null)

    try {
      const response = await runGroupMembersAction(selectedChat.id, {
        action: 'remove_member',
        userId: member.userId
      })

      syncChatParticipantCount(response.participantCount)
      await refreshGroupData()
      toast.success('Member removed from group')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to remove member'
      setErrorMessage(message)
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleLeaveGroup = async () => {
    const currentChatId = selectedChat?.id

    if (!currentChatId || !hasFreshGroupData) {
      return
    }

    setIsLoading(true)
    setErrorMessage(null)

    try {
      const response = await runGroupMembersAction(currentChatId, { action: 'leave' })
      syncChatParticipantCount(response.participantCount)
      setChats((previousChats) => previousChats.filter((chat) => chat.id !== currentChatId))
      removeChatFromAtom(currentChatId)
      setSelectedChat((previousChat) =>
        previousChat?.id === currentChatId ? null : previousChat
      )
      setIsDialogOpen(false)
      toast.success('You left the group chat')
      navigateToHref('/chat')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to leave group chat'
      setErrorMessage(message)
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpdateArchiveState = async (archived: boolean) => {
    if (!selectedChat?.id || !canManageGroup || !hasFreshGroupData) {
      return
    }

    setIsLoading(true)
    setErrorMessage(null)

    try {
      const response = await runGroupMembersAction(selectedChat.id, {
        action: 'update_archive_state',
        archived
      })

      if (response.chat) {
        syncChatState(response.chat)
      }

      toast.success(archived ? 'Group archived' : 'Group restored')
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to update group archive state'
      setErrorMessage(message)
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpdateStackState = async (value: string) => {
    if (!selectedChat?.id || !canManageGroup || !hasFreshGroupData) {
      return
    }

    setIsLoading(true)
    setErrorMessage(null)

    try {
      const response = await runGroupMembersAction(selectedChat.id, {
        action: 'update_stack_state',
        stackWithChatId: value === '__none__' ? null : value
      })

      if (response.chat) {
        syncChatState(response.chat)
      }

      toast.success(value === '__none__' ? 'Group unstacked' : 'Group stacked')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update group stack'
      setErrorMessage(message)
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpdateResponseMode = async (nextResponseMode: GroupChatResponseMode) => {
    if (
      !selectedChat?.id ||
      !canManageGroup ||
      !hasFreshGroupData ||
      responseMode === nextResponseMode
    ) {
      return
    }

    if (nextResponseMode === 'always' && hasTutorMember) {
      toast.error('Tutor group chats must stay in mention mode')
      return
    }

    setIsLoading(true)
    setErrorMessage(null)

    try {
      const response = await updateGroupResponseMode(
        selectedChat.id,
        nextResponseMode
      )

      syncChatState(response.chat)
      setResponseMode(response.responseMode)
      setServerCanManage(true)
      setLoadedChatId(response.chat.id)
      toast.success(
        nextResponseMode === 'mention_only'
          ? 'AI replies now require @Losi mention'
          : 'AI replies now run on every message'
      )
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update response mode'
      setErrorMessage(message)
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  if (!selectedChat || !currentUserId || !isGroupChat) {
    return null
  }

  return (
    <Popover open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <PopoverTrigger asChild>
        <button
          aria-label={canManageGroup ? 'Manage group chat' : 'View group chat members'}
          className="border-border/50 bg-background/90 hover:bg-accent/70 focus-visible:ring-ring flex h-10 items-center rounded-full border px-2 shadow-sm transition-[background-color,box-shadow,transform] duration-200 ease-out hover:shadow-md focus-visible:outline-none focus-visible:ring-2 active:translate-y-px"
          title={canManageGroup ? 'Manage group chat' : 'View group chat members'}
          type="button"
        >
          <GroupAvatarStack
            currentUserId={currentUserId}
            isLoading={isManageStatePending}
            members={orderedMembers}
            size="compact"
          />
          <span className="border-background bg-muted text-muted-foreground ml-1 flex size-7 items-center justify-center rounded-full border-2">
            <IconDots className="size-3.5" stroke={1.8} />
          </span>
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        className="border-border/60 w-[390px] max-w-[calc(100vw-1rem)] rounded-2xl p-0 shadow-xl"
        sideOffset={10}
      >
        <div className="border-border/60 flex items-center gap-3 border-b p-4">
          <GroupAvatarStack
            currentUserId={currentUserId}
            isLoading={isManageStatePending}
            members={orderedMembers}
          />
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">
              {selectedChat.name || 'Group chat'}
            </div>
            <div className="text-muted-foreground text-xs">
              {memberCount || orderedMembers.length} members
            </div>
          </div>
        </div>

        <div className="max-h-[min(72dvh,640px)] space-y-4 overflow-y-auto p-4">
          <div className="space-y-2">
            <div className="text-sm font-medium">AI response mode</div>
            <div className="grid grid-cols-2 gap-2">
              <Button
                className="h-9"
                disabled={isLoading || !canManageGroup || !hasFreshGroupData || hasTutorMember}
                onClick={() => handleUpdateResponseMode('always')}
                title={hasTutorMember ? 'Tutor chats require @Losi mentions' : undefined}
                type="button"
                variant={responseMode === 'always' ? 'default' : 'outline'}
              >
                Always
              </Button>
              <Button
                className="h-9"
                disabled={isLoading || !canManageGroup || !hasFreshGroupData}
                onClick={() => handleUpdateResponseMode('mention_only')}
                type="button"
                variant={responseMode === 'mention_only' ? 'default' : 'outline'}
              >
                Mention
              </Button>
            </div>
            <p className="text-muted-foreground text-xs">
              {hasTutorMember
                ? 'Tutor chats keep Losi quiet until someone includes @Losi.'
                : 'Mention mode keeps Losi quiet until someone includes @Losi.'}
            </p>
          </div>

          <div className="bg-muted/35 border-border/60 space-y-3 rounded-xl border p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-medium">Group visibility</div>
                <p className="text-muted-foreground text-xs">
                  Archived groups stay available at the bottom of the group list.
                </p>
              </div>
              <Switch
                aria-label="Show archived groups"
                checked={showArchivedGroups}
                onCheckedChange={handleToggleArchivedGroups}
              />
            </div>
            {canManageGroup ? (
              <Button
                className="w-full justify-center"
                disabled={isLoading || !hasFreshGroupData}
                onClick={() => handleUpdateArchiveState(!isArchivedGroup)}
                size="sm"
                type="button"
                variant="outline"
              >
                {isArchivedGroup ? (
                  <IconArchiveOff className="mr-2 size-4" />
                ) : (
                  <IconArchive className="mr-2 size-4" />
                )}
                {isArchivedGroup ? 'Restore group' : 'Archive group'}
              </Button>
            ) : null}
          </div>

          {canManageGroup ? (
            <div className="bg-muted/35 border-border/60 space-y-3 rounded-xl border p-3">
              <div>
                <div className="text-sm font-medium">Stack with group</div>
                <p className="text-muted-foreground text-xs">
                  Link related standups or recurring groups into one sidebar stack.
                </p>
              </div>
              <Select
                disabled={isLoading || !hasFreshGroupData}
                onValueChange={handleUpdateStackState}
                value={stackSelectValue}
              >
                <SelectTrigger aria-label="Stack this group with another group">
                  <SelectValue placeholder="No stack" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No manual stack</SelectItem>
                  {stackTargetOptions.map((chat) => (
                    <SelectItem key={chat.id} value={chat.id}>
                      {chat.name || 'Untitled group'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {stackMetadata?.key &&
              stackMetadata.key !== selectedChat.id &&
              !stackMetadata.parentChatId ? (
                <p className="text-muted-foreground text-xs">
                  Automatically stacked by {stackMetadata.label || 'group source'}.
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="bg-muted/35 border-border/60 space-y-2 rounded-xl border p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-medium">Invite link</div>
              {isManageStatePending ? (
                <span className="text-muted-foreground text-xs">Loading</span>
              ) : null}
            </div>
            {isManageStatePending ? (
              <div className="bg-background/70 h-9 animate-pulse rounded-md" />
            ) : canManageGroup ? (
              <>
                <div className="flex items-center gap-2">
                  <Input className="h-9" value={inviteUrl} readOnly />
                  {inviteUrl ? (
                    <CopyButton
                      className="text-foreground size-9 shrink-0"
                      value={inviteUrl}
                      variant="outline"
                    />
                  ) : null}
                  <Button
                    className="size-9 shrink-0"
                    loading={isLoading}
                    onClick={handleRegenerateInviteLink}
                    size="icon"
                    title="Regenerate invite link"
                    type="button"
                    variant="outline"
                  >
                    <IconRefresh className="size-4" />
                  </Button>
                </div>
                {inviteLinkError ? (
                  <p className="text-destructive text-xs">{inviteLinkError}</p>
                ) : null}
                {!inviteUrl && !isRefreshingData && !inviteLinkError ? (
                  <p className="text-muted-foreground text-xs">
                    Regenerate the link to invite more people.
                  </p>
                ) : null}
              </>
            ) : (
              <p className="text-muted-foreground text-xs">
                Only owners and admins can copy or regenerate the invite link.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Members</div>
              {isRefreshingData ? (
                <span className="text-muted-foreground text-xs">Refreshing</span>
              ) : null}
            </div>

            <div className="space-y-1.5">
              {orderedMembers.map((member, index) => {
                const isSelf = member.userId === currentUserId
                const nextAdminRole = member.role === 'admin' ? 'member' : 'admin'
                const nextTutorRole = member.role === 'tutor' ? 'member' : 'tutor'

                return (
                  <div
                    className="border-border/60 bg-background flex items-center gap-3 rounded-xl border px-3 py-2"
                    key={member.userId}
                  >
                    <GroupMemberAvatar className="size-9" index={index} member={member} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">
                        {resolveMemberName(member)}
                      </div>
                      <div className="text-muted-foreground truncate text-xs">
                        {member.email || getRoleLabel(member.role)}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-[11px] font-medium',
                          member.isCreator || member.role === 'owner'
                            ? 'bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200'
                            : member.role === 'tutor'
                              ? 'bg-sky-100 text-sky-900 dark:bg-sky-950/40 dark:text-sky-200'
                              : 'bg-muted text-muted-foreground'
                        )}
                      >
                        {member.isCreator || member.role === 'owner' ? (
                          <IconCrown className="mr-1 inline size-3" />
                        ) : null}
                        {getRoleLabel(member.role)}
                      </span>

                      {canManageGroup && !member.isCreator && !isSelf ? (
                        <Button
                          aria-label={
                            nextAdminRole === 'admin'
                              ? 'Promote member to admin'
                              : 'Demote admin to member'
                          }
                          className="size-8"
                          disabled={isLoading || !hasFreshGroupData}
                          onClick={() => handleUpdateMemberRole(member, nextAdminRole)}
                          size="icon"
                          title={nextAdminRole === 'admin' ? 'Promote to admin' : 'Demote to member'}
                          variant="ghost"
                        >
                          <IconShield className="size-4" />
                        </Button>
                      ) : null}

                      {canManageGroup &&
                      canAssignTutorRole &&
                      !member.isCreator &&
                      !isSelf ? (
                        <Button
                          aria-label={
                            nextTutorRole === 'tutor'
                              ? 'Mark member as tutor'
                              : 'Remove tutor role'
                          }
                          className="size-8"
                          disabled={isLoading || !hasFreshGroupData}
                          onClick={() => handleUpdateMemberRole(member, nextTutorRole)}
                          size="icon"
                          title={nextTutorRole === 'tutor' ? 'Mark as tutor' : 'Remove tutor role'}
                          variant="ghost"
                        >
                          <IconSchool className="size-4" />
                        </Button>
                      ) : null}

                      {canManageGroup && !member.isCreator && !isSelf ? (
                        <Button
                          aria-label="Remove member from group"
                          className="size-8"
                          disabled={isLoading || !hasFreshGroupData}
                          onClick={() => handleRemoveMember(member)}
                          size="icon"
                          title="Remove member"
                          variant="ghost"
                        >
                          <IconTrash className="size-4" />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {errorMessage ? <p className="text-destructive text-sm">{errorMessage}</p> : null}

          <div className="border-border/60 flex items-center justify-between gap-3 border-t pt-3">
            <p className="text-muted-foreground text-xs">
              Up to 20 people can be in this group chat.
            </p>
            {selectedChat.user_id !== currentUserId &&
            !isWorkspaceRoleLoading &&
            isWorkspaceManager ? (
              <p className="text-muted-foreground max-w-44 text-right text-xs">
                Workspace managers keep access here.
              </p>
            ) : selectedChat.user_id === currentUserId ? null : (
              <Button
                className="shrink-0"
                disabled={isLoading || !hasFreshGroupData}
                onClick={handleLeaveGroup}
                size="sm"
                variant="outline"
              >
                <IconLink className="mr-2 size-4" />
                Leave
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
